<template lang="html">
  <v-autocomplete
    ref="autocomplete"
    :attach="attach"
    outlined
    clearable
    deletable-chips
    dense
    multiple
    small-chips
    :readonly="allowModify"
    hide-details
    v-model="selectedForgeTags"
    :items="pickerItems"
    no-filter
    :search-input.sync="searchInput"
    :loading="forgeTagsLoading"
    :menu-props="{ offsetY: true, maxHeight: 400 }"
    @focus="ensureFirstPage"
    @input="updateSource"
    item-text="name"
    item-value="id"
    label="Tags"
    class="forge-tags-selector">
    
    <!-- Custom selection slot with overflow handling -->
    <template v-slot:selection="{ item, index }">
      <template v-if="index < maxVisibleChips">
        <v-chip
          small
          close
          color="primary"
          text-color="white"
          class="ma-1"
          @click:close="removeTag(item.id)">
          <v-icon x-small left>mdi-tag</v-icon>
          <span class="chip-text">{{ item.name }}</span>
        </v-chip>
      </template>
      <v-chip
        v-else-if="index === maxVisibleChips"
        small
        color="grey lighten-2"
        class="ma-1 more-chip">
        <v-icon x-small left>mdi-plus</v-icon>
        {{ selectedForgeTags.length - maxVisibleChips }} more
      </v-chip>
    </template>

    <!-- Custom header in dropdown -->
    <template v-slot:prepend-item>
      <div class="dropdown-header px-4 py-3">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-tag-multiple</v-icon>
            <span class="text-subtitle-2 font-weight-medium">Select Tags</span>
          </div>
          <v-chip 
            v-if="selectedForgeTags.length > 0"
            small 
            color="primary" 
            text-color="white"
            class="selection-count">
            {{ selectedForgeTags.length }} selected
          </v-chip>
        </div>
        <!-- <v-divider class="mt-3"></v-divider> -->
        
        <!-- Selected items section -->
        <!-- <div v-if="selectedForgeTags.length > 0" class="selected-section mt-2">
          <div class="text-caption text-grey mb-1 d-flex align-center">
            <v-icon x-small class="mr-1" color="success">mdi-check-circle</v-icon>
            Selected ({{ selectedForgeTags.length }})
          </div>
        </div> -->
      </div>
    </template>

    <!-- Custom item slot -->
    <template v-slot:item="{ item, on, attrs }">
      <v-list-item 
        v-bind="attrs" 
        v-on="on"
        :class="{ 
          'selected-item': isSelected(item.id),
          'available-item': !isSelected(item.id),
          'first-unselected': isFirstUnselected(item.id)
        }"
        class="tag-list-item">
        <v-list-item-action class="mr-2">
          <v-checkbox
            :input-value="isSelected(item.id)"
            color="primary"
            hide-details
            dense
            readonly>
          </v-checkbox>
        </v-list-item-action>
        <v-list-item-content>
          <div class="d-flex align-center">
            <v-chip
              x-small
              outlined
               color="primary"
              
              class="mr-2 type-chip">
              {{ getType(item.discriminator) }}
            </v-chip>
            <span class="tag-name" :class="{ 'font-weight-medium': isSelected(item.id) }">
              {{ item.name }}
            </span>
          </div>
        </v-list-item-content>
      </v-list-item>
    </template>

    <!-- Next page: loads when the end of the list scrolls into view -->
    <template v-slot:append-item>
      <div v-if="hasMore" v-intersect="onListEnd" class="pa-2 text-center">
        <v-progress-circular v-if="forgeTagsLoading" indeterminate size="18" width="2" color="primary" />
      </div>
    </template>

    <!-- No data slot -->
    <template v-slot:no-data>
      <div class="pa-4 text-center">
        <v-icon large color="grey lighten-1" class="mb-2">mdi-tag-off</v-icon>
        <div class="text-body-2 grey--text">{{ forgeTagsLoading ? 'Loading tags…' : 'No tags available' }}</div>
      </div>
    </template>
  </v-autocomplete>
</template>
<script>
  import { error as logError } from '@/utils/debugLogger';
  import {
    appendUnique, buildTagPickerQuery, hasMorePages, mergePickerItems, missingIds, toPickerTag,
  } from '@Share/utils/tagPickerItems';

  const SEARCH_DEBOUNCE_MS = 300;
  const UNAVAILABLE_TAG_NAME = 'Unavailable tag';

  // Searches the facility's tags on the server, one page of 50 at a time: a facility group can hold
  // 100k+ tags, and loading them all took ~25 s on prod. Only the names of the linked tags are resolved.
  export default {
    name: 'ForgeTagsSelector',
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      attach: {
        type: Boolean,
        default: false,
      },
      allowModify: {
        type: Boolean,
        default: false,
      },
      maxVisibleChips: {
        type: Number,
        default: 2,
      },
    },
    data() {
      return {
        selectedForgeTags: this.value || [],
        // Search results in server order (by name); the selected tags are merged in front by pickerItems.
        results: [],
        total: 0,
        page: 0,
        lastPageSize: 0,
        // The search the loaded results belong to; null until the first page has been requested.
        loadedSearch: null,
        // Results of the empty search, reused when the search box is cleared.
        emptySearchCache: null,
        // id -> { id, name, discriminator } for tags shown as chips (linked or picked).
        resolvedById: {},
        searchInput: null,
        forgeTagsLoading: false,
        requestSeq: 0,
      };
    },
    methods: {
      ensureFirstPage() {
        if (this.loadedSearch === null && !this.forgeTagsLoading) this.loadPage(1, '');
      },
      async loadPage(page, search) {
        if (!this.assetId) return;
        const query = buildTagPickerQuery({ assetId: this.assetId, search, page });
        if (page === 1 && query.search === '' && this.emptySearchCache) {
          this.requestSeq++; // drop any search still in flight
          this.forgeTagsLoading = false;
          Object.assign(this, this.emptySearchCache, { loadedSearch: '' });
          return;
        }
        const seq = ++this.requestSeq;
        this.forgeTagsLoading = true;
        try {
          const data = await this.$store.dispatch('GET_FORGE_TAGS', query);
          if (seq !== this.requestSeq) return; // a newer search has started
          const items = Array.isArray(data?.items) ? data.items : [];
          this.results = appendUnique(page === 1 ? [] : this.results, items);
          // -1 when the API skipped the count (includeTotal: false); older APIs still send a number.
          const total = Number(data?.total);
          this.total = Number.isFinite(total) ? total : -1;
          this.lastPageSize = items.length;
          this.page = query.page;
          this.loadedSearch = query.search;
          if (query.search === '') {
            this.emptySearchCache = {
              results: this.results, total: this.total, page: this.page, lastPageSize: this.lastPageSize,
            };
          }
        } catch (err) {
          if (seq === this.requestSeq) logError('[ForgeTagsSelector] tag search failed', err);
        } finally {
          if (seq === this.requestSeq) this.forgeTagsLoading = false;
        }
      },
      onListEnd(entries, observer, isIntersecting) {
        if (isIntersecting && this.hasMore && !this.forgeTagsLoading) {
          this.loadPage(this.page + 1, this.loadedSearch);
        }
      },
      async resolveSelected() {
        const ids = missingIds(this.selectedForgeTags, this.resolvedById);
        if (!ids.length) return;
        const known = {};
        this.results.forEach((t) => { if (ids.includes(t.id)) known[t.id] = t; });
        const remaining = ids.filter((id) => !known[id]);
        let fetched = [];
        if (remaining.length) {
          try {
            fetched = await this.$store.dispatch('GET_BASE_TAGS_BY_IDS', remaining);
          } catch (err) {
            logError('[ForgeTagsSelector] resolving linked tag names failed', err);
          }
        }
        const resolved = { ...this.resolvedById, ...known };
        (fetched || []).map(toPickerTag).filter(Boolean).forEach((t) => { resolved[t.id] = t; });
        // Ids the server did not return (deleted, or not visible to this user) stay in the value, labelled.
        remaining.forEach((id) => {
          if (!resolved[id]) resolved[id] = { id, name: UNAVAILABLE_TAG_NAME, discriminator: null };
        });
        this.resolvedById = resolved;
      },
      rememberPicked() {
        const resolved = { ...this.resolvedById };
        let changed = false;
        this.results.forEach((t) => {
          if (!resolved[t.id] && this.selectedForgeTags.includes(t.id)) {
            resolved[t.id] = t;
            changed = true;
          }
        });
        if (changed) this.resolvedById = resolved;
      },
      updateSource() {
        this.rememberPicked();
        this.$emit('input', this.selectedForgeTags);
      },
      removeTag(tagId) {
        this.selectedForgeTags = this.selectedForgeTags.filter(id => id !== tagId);
        this.updateSource();
      },
      isSelected(tagId) {
        return this.selectedForgeTags.includes(tagId);
      },
      isFirstUnselected(tagId) {
        // Check if this is the first unselected item in the sorted list
        if (this.selectedForgeTags.length === 0) return false;
        const firstUnselected = this.pickerItems.find(tag => !this.isSelected(tag.id));
        return firstUnselected && firstUnselected.id === tagId;
      },
      getType(discriminator) {
        switch (discriminator) {
          case 'ForgeAnomaly':
            return 'Anomaly';
          case 'ForgeEquipment':
            return 'Equipment';
          case 'ForgePipeline':
            return 'Pipeline';
          case 'ForgeStructural':
            return 'Structural';
          case 'ForgeAvaInspect':
            return 'AVA Inspect';
          default:
            return 'Operation';
        }
      },
      getTypeColor(discriminator) {
        switch (discriminator) {
          case 'ForgeAnomaly':
            return 'red darken-1';
          case 'ForgeEquipment':
            return 'blue darken-1';
          case 'ForgePipeline':
            return 'green darken-1';
          case 'ForgeStructural':
            return 'orange darken-1';
          case 'ForgeAvaInspect':
            return 'purple darken-1';
          default:
            return 'grey darken-1';
        }
      },
      getTypeIcon(discriminator) {
        switch (discriminator) {
          case 'ForgeAnomaly':
            return 'mdi-alert-circle';
          case 'ForgeEquipment':
            return 'mdi-cog';
          case 'ForgePipeline':
            return 'mdi-pipe';
          case 'ForgeStructural':
            return 'mdi-bridge';
          case 'ForgeAvaInspect':
            return 'mdi-magnify-scan';
          default:
            return 'mdi-tag';
        }
      },
    },
    computed: {
      assetId() {
        return this.$store.getters['FACILITY/GET_FACILITY']?.id;
      },
      // Selected tags first (their chips need them in items), then the server results in name order.
      pickerItems() {
        return mergePickerItems(this.selectedForgeTags, this.resolvedById, this.results);
      },
      hasMore() {
        return this.loadedSearch !== null && hasMorePages(this.results.length, this.total, this.lastPageSize);
      },
    },
    watch: {
      value: {
        immediate: true,
        handler(value) {
          this.selectedForgeTags = value || [];
          this.resolveSelected();
        },
      },
      searchInput(search) {
        clearTimeout(this.searchTimer);
        // Nothing to refresh until the list has been opened once.
        if (this.loadedSearch === null && !search) return;
        this.searchTimer = setTimeout(() => this.loadPage(1, search || ''), SEARCH_DEBOUNCE_MS);
      },
      assetId() {
        // Another facility has other tags: drop everything loaded and resolve the linked names again.
        this.requestSeq++;
        this.forgeTagsLoading = false;
        this.results = [];
        this.total = 0;
        this.page = 0;
        this.lastPageSize = 0;
        this.loadedSearch = null;
        this.emptySearchCache = null;
        this.resolvedById = {};
        this.resolveSelected();
      },
    },
    created() {
      this.searchTimer = null; // non-reactive
    },
    beforeDestroy() {
      clearTimeout(this.searchTimer);
    },
  };
</script>
<style lang="scss" scoped>
.forge-tags-selector {
  ::v-deep .v-select__selections {
    flex-wrap: nowrap;
    overflow: hidden;
  }

  .chip-text {
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .more-chip {
    font-weight: 500;
    cursor: pointer;
  }
}

.dropdown-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.selection-count {
  font-weight: 600;
  letter-spacing: 0.5px;
}

.selected-section {
  .text-grey {
    color: #757575;
  }
}

.tag-list-item {
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  margin: 2px 8px;
  border-radius: 8px;

  &:hover {
    background-color: rgba(var(--v-primary-base), 0.08) !important;
  }

  &.selected-item {
    background-color: rgba(var(--v-primary-base), 0.12);
    border-left-color: var(--v-primary-base);

    .tag-name {
      color: var(--v-primary-base);
    }
  }

  &.available-item {
    &:hover {
      border-left-color: #e0e0e0;
    }
  }

  &.first-unselected {
    margin-top: 12px;
    position: relative;

    &::before {
      content: 'Available';
      position: absolute;
      top: -20px;
      left: 0;
      right: 0;
      font-size: 11px;
      color: #757575;
      font-weight: 500;
      padding: 0 8px;
      border-top: 1px solid #e0e0e0;
      padding-top: 6px;
    }
  }
}

.type-chip {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.3px;
  min-width: 90px;
  justify-content: center;
}

.tag-name {
  font-size: 13px;
  color: #424242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

::v-deep .v-list-item__action {
  margin-top: 0;
  margin-bottom: 0;
}

::v-deep .v-input__icon--clear {
  .v-icon {
    font-size: 18px;
  }
}
</style>
